import { DataViewTypes, ProducerTypes, TsPublishMetaData } from '@mode/shared/contract-common';
import { SwitchViewActions } from '@mode/switch-views/contract';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { getPendingDatasetResultUpdates } from '../dataset-result.helpers';
import { DatasetActions } from '@mode/dataset/contract';

// @TODO: (@anthonysimone) [GF] migrate this constant appropriately
export const BROWSER_LIMIT = 15728640; // TODO: change this by moving the constant to angular before PRing

export const datasetResultFeatureKey = 'datasetResult';

export type DatasetResultFeatureState = EntityState<DataViewTypes.DatasetResult>;

const adapter: EntityAdapter<DataViewTypes.DatasetResult> = createEntityAdapter<DataViewTypes.DatasetResult>({
  selectId: (model) => model.switchViewToken, // for easier migration
});

export const initialState: DatasetResultFeatureState = adapter.getInitialState();
export const datasetResultAdapter = adapter;

export const reducer = createReducer(
  initialState,

  on(SwitchViewActions.loadSwitchViewSuccess, (state, action) => adapter.setOne(action.datasetResult, state)),
  on(DatasetActions.loadDatasetSuccess, (state, action) => adapter.setOne(action.datasetResult, state)),
  on(
    DatasetActions.publishDatasetSuccess,
    DatasetActions.initPublishDatasetSuccess,
    DatasetActions.publishDatasetTsSuccess,
    (state, action) => {
      if (!action.switchViewToken) return state;
      const update = {
        id: action.switchViewToken,
        changes: {
          thoughtspot: action.datasetPublicationResponse,
        },
      };
      return adapter.updateOne(update, state);
    }
  ),
  on(DatasetActions.publishDatasetTsInProgress, (state, action) => {
    if (!action.switchViewToken) return state;
    const datasetThoughtspotPublicationToken = action.datasetThoughtspotPublicationToken;
    if (!datasetThoughtspotPublicationToken) return state;
    const update = {
      id: action.switchViewToken,
      changes: {
        progressToastIds: {
          ...state.entities[action.switchViewToken]?.progressToastIds,
          [datasetThoughtspotPublicationToken]: action.progressToastId,
        },
      },
    };
    return adapter.updateOne(update, state);
  }),
  on(DatasetActions.syncTsPublishMetaDataForLocalDataset, (state, action) => {
    const { schemaName, tableName, switchViewToken } = action;
    if (!switchViewToken) return state;
    const update = {
      id: switchViewToken,
      changes: {
        tsPublishMetaData: {
          ...state.entities[switchViewToken]?.tsPublishMetaData,
          ...(schemaName && { schemaName }),
          ...(tableName && { tableName }),
        } as TsPublishMetaData,
      },
    };
    return adapter.updateOne(update, state);
  }),

  on(DatasetActions.unpublishDatasetSuccess, (state, action) => {
    const update = {
      id: action.switchViewToken,
      changes: {
        thoughtspot: undefined,
      },
    };
    return adapter.updateOne(update, state);
  }),

  on(
    SwitchViewActions.pollingSwitchViewCompleted,
    SwitchViewActions.loadVisualizationDataView,
    SwitchViewActions.loadExplorationDataView,
    (state, action) => {
      // This is a temporary logic to updated rather than set an existing
      // temporary datasetresult when one is found by finding a matching queryToken
      if (action.type === SwitchViewActions.pollingSwitchViewCompleted.type) {
        let tempDatasetResultKey = '';

        for (const entityKey in state.entities) {
          const entity = state.entities[entityKey];

          if (entity?.queryToken === action.dataView.datasetResult.queryToken) {
            tempDatasetResultKey = entityKey;
          }
        }

        if (tempDatasetResultKey) {
          const update = {
            id: tempDatasetResultKey,
            changes: action.dataView.datasetResult,
          };
          return adapter.updateOne(update, state);
        }
      }

      return adapter.setOne(action.dataView.datasetResult, state);
    }
  ),

  on(SwitchViewActions.setPendingDatasetResult, (state, action) => {
    const update = {
      id: action.switchViewToken,
      changes: getPendingDatasetResultUpdates(),
    };
    return adapter.updateOne(update, state);
  }),

  on(SwitchViewActions.setPendingDatasetResults, (state, action) => {
    const updates = action.switchViewTokens.map((switchViewToken) => ({
      id: switchViewToken,
      changes: getPendingDatasetResultUpdates(),
    }));
    return adapter.updateMany(updates, state);
  }),

  on(
    SwitchViewActions.loadGfDataViews,
    SwitchViewActions.loadEditorViewsSuccess,
    SwitchViewActions.refreshEditorViewsSuccess,
    SwitchViewActions.loadReportViewDataViews,
    (state, action) => {
      const datasetResults = action.dataViews.map((dataView) => ({
        ...dataView.datasetResult,
        thoughtspot: dataView.thoughtspot,
        tsPublishMetaData: dataView.tsPublishMetaData,
      }));
      return adapter.setMany(datasetResults, state);
    }
  ),

  on(SwitchViewActions.loadImportedDatasetSuccess, (state, action) => {
    return adapter.setOne(action.dataView.datasetResult, state);
  }),

  on(SwitchViewActions.updateLocalQuerySwitchViewTitle, (state, action) => {
    const update = {
      id: action.switchViewToken,
      changes: {
        queryName: action.title,
      },
    };

    return adapter.updateOne(update, state);
  }),

  on(SwitchViewActions.deleteSwitchViewLocalQuery, SwitchViewActions.removeDatasetSuccess, (state, action) => {
    return adapter.removeOne(action.switchViewToken, state);
  }),

  on(SwitchViewActions.removeDatasetFailure, SwitchViewActions.clearDatasetFailure, (state, action) => {
    const update = {
      id: action.switchViewToken,
      changes: {
        error: action.error,
      },
    };
    return adapter.updateOne(update, state);
  }),

  on(SwitchViewActions.importedDatasetRefreshed, (state, action) => {
    const update = {
      id: action.switchView.token,
      changes: {
        ...action.datasetResult,
      },
    };
    return adapter.setOne(action.datasetResult, state);
  }),

  on(SwitchViewActions.addTemporaryLocalQuery, (state, action) => {
    const tempSwitchView: DataViewTypes.DatasetResult = {
      bytes: null,
      completedAt: '',
      contentLength: null,
      createdAt: '',
      csvUrl: '',
      datasourceId: null,
      dbtMetricToken: '',
      executedBy: '',
      executedByEmail: '',
      jsonUrl: '',
      isFallbackData: false,
      isPurged: false,
      metaColumns: [{ name: '', type: '' }],
      oversized: false,
      queryCreatedAt: '',
      queryName: action.queryName,
      queryRunState: DataViewTypes.QueryRunStatus.NotYetRun,
      queryRunToken: '',
      queryToken: action.queryToken,
      isMetric: action.isMetric,
      rawQuery: '',
      renderedQuery: '',
      reportDataUrl: '',
      reportQueryUrl: '',
      rowCount: null,
      switchViewToken: action.queryToken,
      token: '',
      datasetToken: '',
      producerType: null,
      error: null,
    };
    return adapter.addOne(tempSwitchView, state);
  }),

  /*
   *When DatasetActions.loadDatasetReportEditRouteSuccess is called, it is called with a DatasetResult that is based of the last successful data run
   *This is loaded using ReportClientService.getDatasetReportWithViews
   *This helps show the latest data for an exported dataset when in the datasets route
   */
  on(DatasetActions.loadDatasetReportEditRouteSuccess, (state, action) => {
    const datasetResult = action.dataset.result;
    if (datasetResult.producerType === ProducerTypes.NotebookCell) {
      return adapter.setOne(datasetResult, state);
    }
    return state;
  }),

  on(SwitchViewActions.updateTemporaryLocalQueryToken, (state, action) => {
    const update = {
      id: action.tempQueryToken,
      changes: {
        queryToken: action.queryToken,
      },
    };

    return adapter.updateOne(update, state);
  }),

  on(SwitchViewActions.loadQueryRunSuccess, (state, action) => {
    const update = {
      id: action.switchViewToken,
      changes: {
        rawQuery: action.rawSource,
        renderedQuery: action.renderedSource,
        error: action.error,
      },
    };
    return adapter.updateOne(update, state);
  })
);

export const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors();
